/**
 * =============================================================================
 * Página: /afiliado/login
 * Versão: 3.0.0 (Branding por tenant + textos personalizados)
 *
 * Mudanças v3.0.0:
 *   - Lê branding do tenant via `useAffiliateBrandingPublic`
 *     (querystring `?tenant=<uuid>` ou `?programa=<slug>`).
 *   - Aplica CSS vars (cor primária, secundária, gradiente) no escopo
 *     `.affiliate-shell`.
 *   - Substitui logo, título, subtítulo, bullets e rodapé por valores
 *     configurados pelo admin do tenant. Mantém defaults quando ausente.
 *   - Toda a lógica de autenticação, redirecionamentos e tratamento de
 *     erros do guard é preservada.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link, useSearchParams, useParams } from 'react-router-dom';
import {
  affiliateAwaitingPath,
  affiliateForgotPath,
  affiliateSignupPath,
  setLastAffiliateTenantSlug,
} from '@/lib/affiliate-urls';
import {
  Loader2,
  AlertCircle,
  Mail,
  Lock,
  Eye,
  EyeOff,
  MousePointerClick,
  TrendingUp,
  Wallet,
  Sparkles,
  Link2,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import {
  useAffiliateBrandingPublic,
  buildBrandingCssVars,
  type AffiliateBrandingLoginBullet,
} from '@/hooks/useAffiliateBranding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// Defaults (quando o tenant não personalizou nada)
const DEFAULT_BULLETS: AffiliateBrandingLoginBullet[] = [
  { icon: 'link',   text: 'Links únicos por campanha, prontos para divulgar' },
  { icon: 'chart',  text: 'Funil de indicações em tempo real' },
  { icon: 'wallet', text: 'Saques rápidos via PIX' },
];
const DEFAULT_TITLE = 'Indique. Acompanhe. Receba.';
const DEFAULT_SUBTITLE =
  'Painel premium para afiliados acompanharem indicações, comissões e saques em tempo real.';
const DEFAULT_FOOTER = 'Ao entrar, você concorda com os termos do programa de indicação.';

// Mapa de ícones (string → componente lucide)
const ICON_MAP: Record<AffiliateBrandingLoginBullet['icon'], LucideIcon> = {
  link: Link2,
  chart: TrendingUp,
  wallet: Wallet,
  check: Check,
  sparkles: Sparkles,
};

export default function AffiliateLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // tenantSlug vem da URL canônica `/afiliado/:tenantSlug/login` (quando presente)
  const { tenantSlug: tenantSlugParam } = useParams<{ tenantSlug?: string }>();
  const { signIn, ready, affiliate, availableAffiliates } = useAffiliateAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ------------------------------------------------------------------------
  // Branding personalizado por tenant
  // Aceita: rota /afiliado/:tenantSlug/login  OU  ?tenant=<uuid>  OU  ?programa=<slug>
  // ------------------------------------------------------------------------
  const tenantParam = searchParams.get('tenant');
  const programaParam = searchParams.get('programa');
  const { data: brandingData } = useAffiliateBrandingPublic({
    tenantId: tenantParam,
    programSlug: programaParam,
    tenantSlug: tenantSlugParam ?? null,
  });

  // Persiste o último tenantSlug acessado para reutilizar o link bonito
  // em redirecionamentos futuros (logout, sessão expirada, etc.).
  useEffect(() => {
    if (tenantSlugParam) setLastAffiliateTenantSlug(tenantSlugParam);
  }, [tenantSlugParam]);

  const brandingStyle = useMemo(
    () => buildBrandingCssVars(brandingData?.branding),
    [brandingData],
  );
  const logoUrl = brandingData?.branding?.logo_url ?? null;
  const tenantName = brandingData?.tenant_name ?? null;

  const loginCfg = brandingData?.login ?? null;
  const title = loginCfg?.title?.trim() || DEFAULT_TITLE;
  const subtitle = loginCfg?.subtitle?.trim() || DEFAULT_SUBTITLE;
  const footerText = loginCfg?.footer_text?.trim() || DEFAULT_FOOTER;
  const bullets =
    loginCfg?.bullets && loginCfg.bullets.length > 0 ? loginCfg.bullets : DEFAULT_BULLETS;

  // Param `?next=...` (vínculo a um novo programa)
  const nextPath = (() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get('next');
    // só permite paths internos /afiliado/* (defesa contra open redirect)
    if (raw && raw.startsWith('/afiliado/')) return raw;
    return null;
  })();

  // Se chegou aqui já autenticado, redireciona conforme nº de contas
  useEffect(() => {
    if (!ready) return;
    if (availableAffiliates.length > 1) {
      navigate('/afiliado/selecionar-conta', { replace: true });
      return;
    }
    if (affiliate?.status === 'active') {
      navigate(nextPath ?? '/afiliado', { replace: true });
    } else if (affiliate?.status === 'pending') {
      navigate(nextPath ?? affiliateAwaitingPath(tenantSlugParam), { replace: true });
    }
  }, [ready, affiliate, availableAffiliates, navigate, nextPath]);

  // Mensagens de erro vindas do guard (suspended/banned/no_affiliate)
  useEffect(() => {
    const stateError = (location.state as { error?: string } | null)?.error;
    if (stateError === 'suspended') {
      setError('Sua conta está suspensa. Entre em contato com o administrador do programa.');
    } else if (stateError === 'banned') {
      setError('Acesso negado. Sua conta foi banida.');
    } else if (stateError === 'no_affiliate') {
      setError('Sua conta de usuário não está vinculada a um programa de indicação.');
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const result = await signIn(email, password);

    if (!result.success) {
      // v3.1.0 — Quando a edge function indica um destino (ex.: convite
      // pendente), redireciona em vez de exibir apenas o erro cru.
      if (result.redirect_to?.startsWith('/afiliado/')) {
        toast({ title: 'Ação necessária', description: result.error });
        navigate(result.redirect_to, { replace: true });
        setSubmitting(false);
        return;
      }
      setError(result.error ?? 'Falha ao entrar');
      setSubmitting(false);
      return;
    }


    if (!result.can_access) {
      toast({
        title: 'Acesso pendente',
        description: result.message,
      });
      navigate(result.redirect_to ?? affiliateAwaitingPath(tenantSlugParam), { replace: true });
      setSubmitting(false);
      return;
    }

    toast({ title: 'Bem-vindo!', description: result.message });
    navigate(nextPath ?? result.redirect_to ?? '/afiliado', { replace: true });
  };

  return (
    <div
      className="affiliate-shell min-h-[100dvh] grid md:grid-cols-2 bg-[hsl(var(--aff-bg))]"
      style={brandingStyle}
    >
      {/* =========================================================
          HERO — Desktop (lado esquerdo)
          ========================================================= */}
      <aside
        className="aff-hero-surface relative hidden md:flex flex-col justify-between overflow-hidden p-10 lg:p-14"
      >
        {/* Topo: marca / logo do tenant — formato barra (igual sidebar do painel) */}
        <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))]">
          {logoUrl ? (
            <div className="flex flex-col gap-1.5">
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-14 lg:max-h-16 max-w-[220px] object-contain object-left"
              />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-80">
                Painel do Afiliado
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                <Sparkles className="h-5 w-5" />
              </div>
              <span className="text-sm font-semibold tracking-wide opacity-90">
                {tenantName ? `${tenantName} · Painel do Afiliado` : 'Painel do Afiliado'}
              </span>
            </div>
          )}
        </div>

        {/* Centro: headline + bullets */}
        <div className="relative z-10 max-w-md space-y-7 text-[hsl(var(--aff-primary-foreground))]">
          <div className="space-y-3">
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] whitespace-pre-line">
              {title}
            </h1>
            <p className="text-base lg:text-[17px] leading-relaxed opacity-85">
              {subtitle}
            </p>
          </div>

          <ul className="space-y-3">
            {bullets.map((b, i) => {
              const Icon = ICON_MAP[b.icon] ?? Sparkles;
              return (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-[14.5px] leading-relaxed opacity-95">
                    {b.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Rodapé: selo discreto */}
        <div className="relative z-10 flex items-center gap-2 text-[hsl(var(--aff-primary-foreground))] opacity-75">
          <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
          <span className="text-xs font-medium tracking-wide uppercase">
            Programa de Indicação
          </span>
        </div>
      </aside>

      {/* =========================================================
          HERO — Mobile (mini-faixa no topo)
          ========================================================= */}
      <div className="aff-hero-surface md:hidden relative h-[34dvh] min-h-[220px] flex items-end overflow-hidden px-6 pb-10">
        <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))] space-y-2">
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-10 max-w-[160px] object-contain object-left"
              />
            ) : (
              <>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                  <Sparkles className="h-4 w-4" />
                </div>
                <span className="text-[12px] font-semibold tracking-wide opacity-90">
                  {tenantName ? `${tenantName} · Afiliado` : 'Painel do Afiliado'}
                </span>
              </>
            )}
          </div>
          <h1 className="text-[26px] font-bold tracking-tight leading-tight">
            Bem-vindo de volta
          </h1>
        </div>
      </div>

      {/* =========================================================
          COLUNA DO FORMULÁRIO
          ========================================================= */}
      <main
        className={cn(
          'flex justify-center px-4 pb-8 md:p-10 lg:p-14',
          '-mt-10 md:mt-0 relative z-10',
          'md:items-center md:justify-center',
        )}
      >
        <div className="w-full max-w-md">
          <div
            className="aff-card rounded-3xl p-6 md:p-8 space-y-6 aff-animate-in"
            style={{ boxShadow: 'var(--aff-shadow-hero)' }}
          >
            <div className="space-y-1.5">
              <h2 className="text-[22px] md:text-[24px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                Entrar na sua conta
              </h2>
              <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                Use seu e-mail e senha para acessar o painel.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-2xl bg-[hsl(var(--aff-danger)/0.10)] px-3.5 py-2.5 text-[13px] text-[hsl(var(--aff-danger))]"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="leading-snug">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label
                  htmlFor="email"
                  className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                >
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    autoComplete="email"
                    disabled={submitting}
                    className="h-11 pl-10 pr-3 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="password"
                  className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                >
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    disabled={submitting}
                    minLength={8}
                    className="h-11 pl-10 pr-11 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-[hsl(var(--aff-muted-foreground))] hover:text-[hsl(var(--aff-foreground))] hover:bg-[hsl(var(--aff-surface-2))] transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="-mt-2 flex justify-end">
                <Link
                  to={affiliateForgotPath(tenantSlugParam)}
                  className="text-[12.5px] font-semibold text-[hsl(var(--aff-primary))] hover:underline underline-offset-2"
                >
                  Esqueci minha senha
                </Link>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 border-0 font-semibold text-[15px] shadow-[var(--aff-shadow-soft)] transition-opacity"
                style={{ background: 'var(--aff-gradient-hero)' }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>

            <div className="border-t border-[hsl(var(--aff-border))] pt-4 text-center">
              <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                Não tem conta?{' '}
                <Link
                  to={affiliateSignupPath(tenantSlugParam)}
                  className="font-semibold text-[hsl(var(--aff-primary))] hover:underline underline-offset-2"
                >
                  Saiba como se cadastrar
                </Link>
              </p>
            </div>
          </div>

          {/* Rodapé discreto — texto personalizado pelo tenant */}
          <p className="mt-6 text-center text-[11px] text-[hsl(var(--aff-muted-foreground))]">
            {footerText}
          </p>
        </div>
      </main>
    </div>
  );
}
